/**
 * Master Tenants — list, create, edit, suspend, reset-password.
 *
 * The "create tenant" modal is THE canonical onboarding flow for the agency:
 * fill name + email + pick package → backend returns provisional password
 * exactly once → master copies + shares with the client.
 */
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Search, Copy, KeyRound, Trash2, Eye, EyeOff,
  Loader2, Check, X, Crown, Users, AlertTriangle, LogIn, Settings, Mail,
} from "lucide-react";
import MasterLayout from "./MasterLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "alp.access_token";

const STATUS_PILL = {
  active:    { bg: "#DCFCE7", text: "#15803D", label: "Active" },
  paused:    { bg: "#FEF3C7", text: "#92400E", label: "Paused" },
  cancelled: { bg: "#F3F4F6", text: "#6B7280", label: "Cancelled" },
};

export default function MasterTenants() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tenants, setTenants] = useState([]);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [credsModal, setCredsModal] = useState(null); // { email, provisional_password, kind: 'create'|'reset' }
  const [editTenant, setEditTenant] = useState(null); // tenant being edited inline
  const [catalog, setCatalog] = useState({ agents: [], modules: [] });

  // URL-driven filters (Master Dashboard tiles link here with `?pkg=` / `?status=`)
  const pkgFilter    = (searchParams.get("pkg")    || "").toLowerCase();
  const statusFilter = (searchParams.get("status") || "").toLowerCase();

  const load = async () => {
    setLoading(true);
    try {
      const [t, p, c] = await Promise.all([
        axios.get(`${API}/master/tenants`),
        axios.get(`${API}/master/packages`),
        axios.get(`${API}/master/catalog`),
      ]);
      setTenants(t.data.tenants || []);
      setPackages(p.data.packages || []);
      setCatalog({ agents: c.data.agents || [], modules: c.data.modules || [] });
    } catch (e) {
      toast.error("Failed to load tenants");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = tenants;
    if (pkgFilter) {
      list = list.filter((t) => {
        const slug = String(t.package_slug || t.package?.slug || "").toLowerCase();
        const id   = String(t.package_id   || t.package?.id   || "").toLowerCase();
        return slug === pkgFilter || id === pkgFilter;
      });
    }
    if (statusFilter) {
      list = list.filter((t) => String(t.status || "").toLowerCase() === statusFilter);
    }
    if (!q) return list;
    return list.filter((t) =>
      [t.name, t.owner_email, t.company_name, t.sector].some(
        (f) => (f || "").toLowerCase().includes(q),
      ),
    );
  }, [tenants, search, pkgFilter, statusFilter]);

  const pkgLabel = useMemo(() => {
    if (!pkgFilter) return "";
    const p = packages.find((x) => String(x.slug || "").toLowerCase() === pkgFilter || String(x.id || "").toLowerCase() === pkgFilter);
    return p?.name || pkgFilter;
  }, [packages, pkgFilter]);

  const clearUrlFilters = () => {
    const next = new URLSearchParams(searchParams);
    next.delete("pkg"); next.delete("status");
    setSearchParams(next, { replace: true });
  };

  const onResetPassword = async (tenant) => {
    if (!window.confirm(`Reset ${tenant.owner_email}'s password?`)) return;
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenant.id}/reset-password`);
      setCredsModal({ ...data.credentials, kind: "reset" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    }
  };

  const onSuspend = async (tenant) => {
    const next = tenant.status === "paused" ? "active" : "paused";
    try {
      await axios.patch(`${API}/master/tenants/${tenant.id}`, { status: next });
      toast.success(`Tenant ${next === "paused" ? "paused" : "reactivated"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (tenant) => {
    if (tenant.is_master_workspace) {
      toast.error("Cannot delete the master workspace");
      return;
    }
    if (!window.confirm(`Cancel access for ${tenant.name}? This is reversible.`)) return;
    try {
      await axios.delete(`${API}/master/tenants/${tenant.id}`);
      toast.success("Tenant cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const onImpersonate = async (tenant) => {
    if (!window.confirm(`Login as ${tenant.name} in READ-ONLY mode?\nWrite actions will be blocked.`)) return;
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenant.id}/impersonate`);
      if (data?.access_token) {
        try { window.localStorage.setItem(TOKEN_KEY, data.access_token); } catch (_e) { /* ignore */ }
      }
      toast.success(`Now viewing ${tenant.name} (read-only)`);
      // Hard reload so AuthContext re-hydrates with the impersonation token.
      window.location.href = "/dashboard";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impersonate failed");
    }
  };

  const onResendOnboarding = async (tenant) => {
    if (!window.confirm(`Resend the welcome email to ${tenant.owner_email}?\nThis will rotate to a NEW provisional password.`)) return;
    try {
      const { data } = await axios.post(`${API}/master/tenants/${tenant.id}/resend-onboarding-email`);
      const sim = data?.onboarding_email?.simulated;
      const ok = data?.onboarding_email?.ok;
      if (sim) {
        // Show creds for manual share (simulated mode = no provider configured).
        setCredsModal({
          email: data.credentials.email,
          provisional_password: data.credentials.provisional_password,
          kind: "resend",
        });
        toast.warning("Email simulated — copy credentials below to share manually");
      } else if (ok) {
        toast.success(`Welcome email re-sent to ${tenant.owner_email}`);
      } else {
        toast.error("Resend failed — check Master Logs");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    }
  };

  return (
    <MasterLayout title="Clients">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold tracking-tight">Clients</h2>
          <p className="text-[12.5px] text-white/55 mt-1">Manage tenant access, packages, and provisional passwords.</p>
        </div>
        <button
          data-testid="master-tenant-new"
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold hover:bg-[#D9B25E] transition-colors"
        >
          <Plus className="w-4 h-4" /> New Client
        </button>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative max-w-md flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="master-tenant-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name, email, sector…"
            className="w-full h-10 pl-10 pr-3 rounded-lg border bg-white/5 text-white placeholder:text-white/40 text-[13px] focus:outline-none focus:border-[#C9A24E]"
            style={{ borderColor: "rgba(201,162,78,0.2)" }}
          />
        </div>
        {(pkgFilter || statusFilter) && (
          <div
            data-testid="master-tenant-url-filter-chip"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-full text-[12px] font-semibold text-[#C9A24E] border"
            style={{ borderColor: "rgba(201,162,78,0.35)", background: "rgba(201,162,78,0.08)" }}
          >
            {pkgFilter && <span>Package: <span className="text-white">{pkgLabel}</span></span>}
            {pkgFilter && statusFilter && <span className="text-white/30">•</span>}
            {statusFilter && <span>Status: <span className="text-white capitalize">{statusFilter}</span></span>}
            <button
              type="button"
              onClick={clearUrlFilters}
              data-testid="master-tenant-url-filter-clear"
              className="ml-1 w-5 h-5 rounded-full flex items-center justify-center hover:bg-white/10"
              title="Limpar filtro"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-white/55 text-[13px]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="rounded-xl border overflow-x-auto" style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-left border-b" style={{ borderColor: "rgba(201,162,78,0.12)" }}>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Client</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Email</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Package</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Status</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Leads</th>
                <th className="px-4 py-3 font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Last login</th>
                <th className="px-4 py-3 text-right font-bold uppercase text-[10.5px] tracking-wider text-[#C9A24E]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const pkg = packages.find((p) => p.id === t.package_id);
                const status = STATUS_PILL[t.status] || STATUS_PILL.active;
                return (
                  <tr key={t.id} data-testid={`master-tenant-row-${t.slug}`}
                      className="border-b hover:bg-white/5 transition-colors"
                      style={{ borderColor: "rgba(201,162,78,0.08)" }}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        {t.is_master_workspace && <Crown className="w-3.5 h-3.5 text-[#C9A24E]" />}
                        <div>
                          <div className="font-semibold">{t.name}</div>
                          <div className="text-[10.5px] text-white/45">{t.company_name || t.sector}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-white/75">
                      {t.owner_email}
                      {t.must_change_password && (
                        <span className="ml-2 inline-flex items-center gap-0.5 text-[9.5px] px-1 py-0.5 rounded bg-amber-500/20 text-amber-300 font-bold">
                          <KeyRound className="w-2.5 h-2.5" /> 1st login
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <span className="inline-block text-[10.5px] font-bold px-2 py-0.5 rounded" style={{
                        background: `${pkg?.color || "#6B7280"}26`,
                        color: pkg?.color || "#9CA3AF",
                      }}>{pkg?.name || t.package_slug || "—"}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-[10.5px] font-bold px-2 py-0.5 rounded"
                        style={{ background: status.bg, color: status.text }}>{status.label}</span>
                    </td>
                    <td className="px-4 py-3 text-white/75">{t.leads_count}</td>
                    <td className="px-4 py-3 text-white/55 text-[11.5px]">
                      {t.last_login_at ? new Date(t.last_login_at).toLocaleString() : <span className="text-white/30">never</span>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        {!t.is_master_workspace && (
                          <button data-testid={`master-tenant-impersonate-${t.slug}`}
                            onClick={() => onImpersonate(t)}
                            className="px-2 py-1 rounded text-[11px] hover:bg-emerald-500/15 text-emerald-300 inline-flex items-center gap-1"
                            title="Login as client (read-only)">
                            <LogIn className="w-3 h-3" />
                          </button>
                        )}
                        <button data-testid={`master-tenant-edit-${t.slug}`}
                          onClick={() => setEditTenant(t)}
                          className="px-2 py-1 rounded text-[11px] hover:bg-white/10 text-sky-300 inline-flex items-center gap-1"
                          title="Edit package & grants">
                          <Settings className="w-3 h-3" />
                        </button>
                        {!t.is_master_workspace && (
                          <button data-testid={`master-tenant-resend-${t.slug}`}
                            onClick={() => onResendOnboarding(t)}
                            className="px-2 py-1 rounded text-[11px] hover:bg-purple-500/15 text-purple-300 inline-flex items-center gap-1"
                            title="Resend onboarding email (rotates password)">
                            <Mail className="w-3 h-3" />
                          </button>
                        )}
                        <button data-testid={`master-tenant-reset-${t.slug}`}
                          onClick={() => onResetPassword(t)}
                          className="px-2 py-1 rounded text-[11px] hover:bg-white/10 text-amber-300 inline-flex items-center gap-1"
                          title="Reset provisional password">
                          <KeyRound className="w-3 h-3" />
                        </button>
                        {!t.is_master_workspace && (
                          <>
                            <button onClick={() => onSuspend(t)}
                              data-testid={`master-tenant-toggle-${t.slug}`}
                              className="px-2 py-1 rounded text-[11px] hover:bg-white/10 text-white/65 inline-flex items-center gap-1"
                              title={t.status === "paused" ? "Reactivate" : "Pause"}>
                              {t.status === "paused" ? <Check className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
                            </button>
                            <button onClick={() => onDelete(t)}
                              data-testid={`master-tenant-delete-${t.slug}`}
                              className="px-2 py-1 rounded text-[11px] hover:bg-red-500/15 text-red-300 inline-flex items-center gap-1"
                              title="Cancel">
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-white/45">
                  <Users className="w-8 h-8 mx-auto mb-2 text-white/30" />
                  No clients yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {createOpen && (
        <CreateTenantModal
          packages={packages}
          onClose={() => setCreateOpen(false)}
          onCreated={(creds) => { setCredsModal({ ...creds, kind: "create" }); load(); setCreateOpen(false); }}
        />
      )}

      {credsModal && <CredentialsRevealModal creds={credsModal} onClose={() => setCredsModal(null)} />}
      {editTenant && (
        <EditTenantModal
          tenant={editTenant}
          packages={packages}
          catalog={catalog}
          onClose={() => setEditTenant(null)}
          onSaved={() => { setEditTenant(null); load(); }}
        />
      )}
    </MasterLayout>
  );
}

// ----------------------------------------------------------------------------

function CreateTenantModal({ packages, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [sector, setSector] = useState("");
  const [packageId, setPackageId] = useState(packages.find((p) => p.slug === "basico")?.id || "");
  const [forceChange, setForceChange] = useState(true);
  const [sendWelcome, setSendWelcome] = useState(true);
  const [welcomeLang, setWelcomeLang] = useState("pt");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name || !email) return;
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/master/tenants`, {
        name, company_name: company, owner_email: email, phone, sector,
        package_id: packageId, require_password_change: forceChange,
        send_welcome_email: sendWelcome, welcome_email_lang: welcomeLang,
      });
      const oe = data.onboarding_email || {};
      if (sendWelcome && oe.simulated) {
        toast.warning("Client created — email simulated (configure provider). Copy credentials below.");
      } else if (sendWelcome && oe.ok) {
        toast.success(`Client created · welcome email sent to ${email}`);
      } else {
        toast.success("Client created");
      }
      onCreated(data.credentials);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="master-tenant-create-modal"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg rounded-xl border bg-[#1A0E36] p-6 space-y-4"
        style={{ borderColor: "rgba(201,162,78,0.3)" }}>
        <div className="flex items-center justify-between">
          <h3 className="text-white font-extrabold text-lg flex items-center gap-2">
            <Plus className="w-5 h-5 text-[#C9A24E]" /> New Client
          </h3>
          <button type="button" onClick={onClose} className="text-white/55 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <Field label="Name *" value={name} onChange={setName} testid="create-tenant-name" required autoFocus />
          <Field label="Company" value={company} onChange={setCompany} testid="create-tenant-company" />
          <Field label="Owner email *" type="email" value={email} onChange={setEmail} testid="create-tenant-email" required />
          <Field label="Phone" value={phone} onChange={setPhone} testid="create-tenant-phone" />
          <Field label="Sector" value={sector} onChange={setSector} testid="create-tenant-sector" />
          <label>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Package</div>
            <select
              data-testid="create-tenant-package"
              value={packageId}
              onChange={(e) => setPackageId(e.target.value)}
              className="w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
              style={{ borderColor: "rgba(201,162,78,0.2)" }}
            >
              {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#1A0E36]">{p.name}</option>)}
            </select>
          </label>
        </div>

        <label className="flex items-center gap-2 text-[12px] text-white/75">
          <input type="checkbox" checked={forceChange} onChange={(e) => setForceChange(e.target.checked)} data-testid="create-tenant-force-change" />
          Require client to change password on first login
        </label>

        <div className="rounded-lg border p-3 space-y-2"
             style={{ borderColor: "rgba(201,162,78,0.18)", background: "rgba(255,255,255,0.03)" }}>
          <label className="flex items-center gap-2 text-[12.5px] text-white/85">
            <input type="checkbox" checked={sendWelcome}
              onChange={(e) => setSendWelcome(e.target.checked)}
              data-testid="create-tenant-send-welcome" />
            <Mail className="w-3.5 h-3.5 text-[#C9A24E]" />
            Send welcome email automatically
          </label>
          {sendWelcome && (
            <label className="flex items-center gap-2 pl-6 text-[11.5px] text-white/65">
              Language:
              <select value={welcomeLang} onChange={(e) => setWelcomeLang(e.target.value)}
                data-testid="create-tenant-welcome-lang"
                className="h-7 rounded border bg-white/5 text-white px-2 text-[11.5px]"
                style={{ borderColor: "rgba(201,162,78,0.2)" }}>
                <option value="pt" className="bg-[#1A0E36]">PT (Português)</option>
                <option value="en" className="bg-[#1A0E36]">EN (English)</option>
                <option value="es" className="bg-[#1A0E36]">ES (Español)</option>
              </select>
            </label>
          )}
          <p className="text-[10px] text-white/45 pl-6 italic">
            If no email provider is configured, the email will be SIMULATED and you can copy credentials manually.
          </p>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-lg text-white/65 hover:bg-white/5 text-[12.5px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy || !name || !email}
            data-testid="create-tenant-submit"
            className="h-10 px-5 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold hover:bg-[#D9B25E] disabled:opacity-50 inline-flex items-center gap-2 text-[12.5px]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Create & generate password
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, value, onChange, testid, type = "text", required, autoFocus }) {
  return (
    <label>
      <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">{label}</div>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)}
        data-testid={testid} required={required} autoFocus={autoFocus}
        className="w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px] focus:outline-none focus:border-[#C9A24E]"
        style={{ borderColor: "rgba(201,162,78,0.2)" }}
      />
    </label>
  );
}

function CredentialsRevealModal({ creds, onClose }) {
  const [showPw, setShowPw] = useState(false);
  const copy = (txt) => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(txt).then(() => toast.success("Copied!"))
                                            .catch(() => fallbackCopy(txt));
      } else {
        fallbackCopy(txt);
      }
    } catch (_e) { fallbackCopy(txt); }
  };
  const fallbackCopy = (txt) => {
    const ta = document.createElement("textarea");
    ta.value = txt; ta.style.position = "fixed"; ta.style.opacity = "0";
    document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); toast.success("Copied!"); }
    catch (_e) { toast.error("Copy failed"); }
    finally { document.body.removeChild(ta); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div data-testid="master-creds-reveal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md rounded-xl border bg-[#1A0E36] p-6 space-y-4"
        style={{ borderColor: "rgba(201,162,78,0.3)" }}>
        <div className="flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-[#C9A24E]" />
          <h3 className="text-white font-extrabold text-lg">
            {creds.kind === "reset" ? "Password reset" : "Client created"}
          </h3>
        </div>
        <div className="text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
          ⚠️ This password is shown <strong>only once</strong>. Copy it now and share securely.
        </div>
        <div className="space-y-3 text-[13px]">
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Email</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-white/5 text-white" data-testid="master-creds-email">{creds.email}</code>
              <button onClick={() => copy(creds.email)} className="p-2 rounded hover:bg-white/10 text-[#C9A24E]"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Provisional password</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 px-3 py-2 rounded bg-white/5 text-white font-mono" data-testid="master-creds-password">
                {showPw ? creds.provisional_password : "•".repeat(creds.provisional_password?.length || 12)}
              </code>
              <button onClick={() => setShowPw((v) => !v)} className="p-2 rounded hover:bg-white/10 text-white/65">
                {showPw ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => copy(creds.provisional_password)} className="p-2 rounded hover:bg-white/10 text-[#C9A24E]" data-testid="master-creds-copy"><Copy className="w-3.5 h-3.5" /></button>
            </div>
          </div>
        </div>
        <div className="flex justify-end pt-2">
          <button onClick={onClose} data-testid="master-creds-close"
            className="h-10 px-5 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold hover:bg-[#D9B25E] text-[12.5px]">
            Done
          </button>
        </div>
      </div>
    </div>
  );
}


// ----------------------------------------------------------------------------

function EditTenantModal({ tenant, packages, catalog, onClose, onSaved }) {
  const [packageId, setPackageId] = useState(tenant.package_id || "");
  const [agentGrants, setAgentGrants] = useState(tenant.agent_grants || []);
  const [moduleGrants, setModuleGrants] = useState(tenant.module_grants || []);
  const [leadsPerMonth, setLeadsPerMonth] = useState(tenant.leads_per_month || 0);
  const [status, setStatus] = useState(tenant.status || "active");
  const [branding, setBranding] = useState(tenant.branding || {});
  const [busy, setBusy] = useState(false);

  // Detect Premium based on selected package's slug — branding section
  // only renders for premium tenants.
  const selectedPackage = packages.find((p) => p.id === packageId);
  const isPremium = (selectedPackage?.slug || tenant.package_slug || "").toLowerCase() === "premium";

  const toggle = (list, setList, id) =>
    setList(list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  const onPackageChange = (newPkgId) => {
    setPackageId(newPkgId);
    const pkg = packages.find((p) => p.id === newPkgId);
    if (pkg) {
      // Snap grants to the chosen package defaults — master can still override below.
      setAgentGrants(pkg.agents || []);
      setModuleGrants(pkg.modules || []);
      setLeadsPerMonth(pkg.leads_per_month || 0);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.patch(`${API}/master/tenants/${tenant.id}`, {
        package_id: packageId,
        agent_grants: agentGrants,
        module_grants: moduleGrants,
        leads_per_month: leadsPerMonth,
        status,
        branding: isPremium ? branding : undefined,
      });
      toast.success("Tenant updated");
      onSaved();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Update failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <form data-testid="master-tenant-edit-modal"
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border bg-[#1A0E36] p-6 space-y-4"
        style={{ borderColor: "rgba(201,162,78,0.3)" }}>
        <div className="flex items-center justify-between sticky top-0 bg-[#1A0E36] -mt-1 pt-1 pb-2 z-10">
          <h3 className="text-white font-extrabold text-lg flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#C9A24E]" /> Edit · {tenant.name}
          </h3>
          <button type="button" onClick={onClose} className="text-white/55 hover:text-white"><X className="w-4 h-4" /></button>
        </div>

        <div className="grid grid-cols-2 gap-3 text-[12.5px]">
          <label>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Package</div>
            <select
              data-testid="edit-tenant-package"
              value={packageId}
              onChange={(e) => onPackageChange(e.target.value)}
              className="w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
              style={{ borderColor: "rgba(201,162,78,0.2)" }}
            >
              {packages.map((p) => <option key={p.id} value={p.id} className="bg-[#1A0E36]">{p.name}</option>)}
            </select>
          </label>
          <label>
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Status</div>
            <select
              data-testid="edit-tenant-status"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
              style={{ borderColor: "rgba(201,162,78,0.2)" }}
            >
              <option value="active" className="bg-[#1A0E36]">active</option>
              <option value="paused" className="bg-[#1A0E36]">paused</option>
              <option value="cancelled" className="bg-[#1A0E36]">cancelled</option>
            </select>
          </label>
          <label className="col-span-2">
            <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Leads/month (0 = unlimited)</div>
            <input
              data-testid="edit-tenant-leads-per-month"
              type="number" min="0"
              value={leadsPerMonth}
              onChange={(e) => setLeadsPerMonth(parseInt(e.target.value || "0", 10))}
              className="w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]"
              style={{ borderColor: "rgba(201,162,78,0.2)" }}
            />
          </label>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-2">Agents ({agentGrants.length}/{catalog.agents.length})</div>
          <div className="grid grid-cols-2 gap-2">
            {catalog.agents.map((a) => (
              <label key={a.id} className="flex items-start gap-2 p-2 rounded border cursor-pointer hover:bg-white/5"
                     style={{ borderColor: agentGrants.includes(a.id) ? "#C9A24E" : "rgba(201,162,78,0.15)" }}>
                <input
                  type="checkbox"
                  data-testid={`edit-tenant-agent-${a.id}`}
                  checked={agentGrants.includes(a.id)}
                  onChange={() => toggle(agentGrants, setAgentGrants, a.id)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-[12px] text-white font-semibold">{a.label}</div>
                  <div className="text-[10.5px] text-white/55">{a.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <div>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-2">Modules ({moduleGrants.length}/{catalog.modules.length})</div>
          <div className="grid grid-cols-3 gap-2">
            {catalog.modules.map((m) => (
              <label key={m.id} className="flex items-center gap-2 p-2 rounded border cursor-pointer hover:bg-white/5"
                     style={{ borderColor: moduleGrants.includes(m.id) ? "#C9A24E" : "rgba(201,162,78,0.15)" }}>
                <input
                  type="checkbox"
                  data-testid={`edit-tenant-module-${m.id}`}
                  checked={moduleGrants.includes(m.id)}
                  onChange={() => toggle(moduleGrants, setModuleGrants, m.id)}
                />
                <span className="text-[12px] text-white">{m.label}</span>
              </label>
            ))}
          </div>
        </div>

        {isPremium && (
          <BrandingFields branding={branding} setBranding={setBranding} tenantSlug={tenant.slug} />
        )}

        <div className="flex justify-end gap-2 pt-2 sticky bottom-0 bg-[#1A0E36] pb-1">
          <button type="button" onClick={onClose}
            className="h-10 px-4 rounded-lg text-white/65 hover:bg-white/5 text-[12.5px] font-semibold">Cancel</button>
          <button type="submit" disabled={busy}
            data-testid="edit-tenant-submit"
            className="h-10 px-5 rounded-lg bg-[#C9A24E] text-[#0F0820] font-bold hover:bg-[#D9B25E] disabled:opacity-50 inline-flex items-center gap-2 text-[12.5px]">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </form>
    </div>
  );
}

function BrandingFields({ branding, setBranding, tenantSlug }) {
  const set = (k, v) => setBranding({ ...branding, [k]: v });
  const inputCls = "w-full h-9 rounded border bg-white/5 text-white px-2 text-[12.5px]";
  const inputStyle = { borderColor: "rgba(201,162,78,0.2)" };
  const fields = [
    { k: "logo_url", lbl: "Logo URL", type: "url", ph: "https://…/logo.png" },
    { k: "tagline", lbl: "Tagline", type: "text", ph: "FIND. ENGAGE. GROW." },
    { k: "hero_eyebrow", lbl: "Hero eyebrow", type: "text", ph: "B2B prospecting" },
    { k: "hero_h1", lbl: "Hero headline (H1)", type: "text", ph: "Generate qualified leads — fast." },
    { k: "hero_sub", lbl: "Hero subhead", type: "text", ph: "Short pitch under the H1…" },
    { k: "hero_cta", lbl: "Hero CTA copy", type: "text", ph: "Start trial" },
  ];
  return (
    <div data-testid="edit-tenant-branding-section" className="space-y-3 rounded-lg border p-4"
         style={{ borderColor: "rgba(201,162,78,0.3)", backgroundColor: "rgba(201,162,78,0.05)" }}>
      <div className="flex items-center justify-between">
        <div className="text-[10.5px] uppercase tracking-wider font-bold text-[#C9A24E]">Premium · White-label landing</div>
        {tenantSlug && (
          <a href={`/p/${tenantSlug}`} target="_blank" rel="noreferrer"
            data-testid="edit-tenant-branding-preview"
            className="text-[11px] font-bold text-[#C9A24E] hover:underline">/p/{tenantSlug} ↗</a>
        )}
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Primary color</div>
          <div className="flex items-center gap-2">
            <input type="color" value={branding.primary_color || "#0F0820"}
              onChange={(e) => set("primary_color", e.target.value)}
              data-testid="edit-tenant-branding-primary-color"
              className="w-10 h-9 rounded border" style={{ borderColor: "rgba(201,162,78,0.2)" }} />
            <input type="text" value={branding.primary_color || ""} placeholder="#0F0820"
              onChange={(e) => set("primary_color", e.target.value)}
              className={inputCls} style={inputStyle} />
          </div>
        </label>
        <label>
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">Accent color</div>
          <div className="flex items-center gap-2">
            <input type="color" value={branding.accent_color || "#C9A24E"}
              onChange={(e) => set("accent_color", e.target.value)}
              data-testid="edit-tenant-branding-accent-color"
              className="w-10 h-9 rounded border" style={{ borderColor: "rgba(201,162,78,0.2)" }} />
            <input type="text" value={branding.accent_color || ""} placeholder="#C9A24E"
              onChange={(e) => set("accent_color", e.target.value)}
              className={inputCls} style={inputStyle} />
          </div>
        </label>
      </div>
      {fields.map((f) => (
        <label key={f.k} className="block">
          <div className="text-[10.5px] uppercase tracking-wider font-bold text-white/55 mb-1">{f.lbl}</div>
          <input type={f.type} value={branding[f.k] || ""} placeholder={f.ph}
            onChange={(e) => set(f.k, e.target.value)}
            data-testid={`edit-tenant-branding-${f.k.replace(/_/g, "-")}`}
            className={inputCls} style={inputStyle} />
        </label>
      ))}
    </div>
  );
}
